import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { Graph, Product } from 'schema-dts'
import type { EventDescription } from '~/composables/types/event'

const useKitProductSchema = (kits?: EventDescription[]): any => {
  const schema = ref<Graph | undefined>(undefined)
  const script = computed(() => ({
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))
  const fullLocalizedUrl = useFullLocalizedUrl()

  const createSchema = (): Product[] => {
    if (!kits || !Array.isArray(kits)) {
      return []
    }

    return kits.map((kit) => {
      dayjs.extend(utc)
      dayjs.extend(timezone)

      const reviewSchemaArr = eventGetters.getReviewsList(kit).map((review) => {
        return {
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: reviewGetters.getReviewRating(review),
          },
          reviewBody: reviewGetters.getReview(review),
          author: {
            '@type': 'Person',
            name: reviewGetters.getAuthor(review),
          },
        }
      })

      const images = kit.main_image?.thumb
        ? [kit.main_image.thumb]
        : eventGetters.getGallery(kit).map((x) => x.cropped)

      const obj = {
        '@type': 'Product',
        name: eventGetters.getTitle(kit),
        url: fullLocalizedUrl(`/k/${eventGetters.getPermalink(kit)}/`),
        image: images,
        description: eventGetters.getSubtitle(kit),
        offers: {
          '@type': 'Offer',
          availability: 'InStock',
          url: fullLocalizedUrl(`/k/${eventGetters.getPermalink(kit)}/`),
          price: eventGetters.getDefaultPriceAmount(kit) / 100,
          priceCurrency: eventGetters.getDefaultPriceCurrency(kit),
          priceSpecification: eventGetters.getSchemaPriceSpecification(kit),
        },
        brand: {
          '@type': 'Brand',
          name:
            supplierGetters.getName(eventGetters.getSupplier(kit)) ||
            kit?.supplier,
          url: fullLocalizedUrl(
            `/p/${supplierGetters.getPermalink(eventGetters.getSupplier(kit))}`
          ),
        },
        ...(eventGetters.getReviewsTotal(kit) &&
          eventGetters.getReviewsTotal(kit) > 0 && {
            review: reviewSchemaArr,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: eventGetters.getReviewsAverage(kit, 'en-US'),
              bestRating: 5,
              worstRating: 1,
              ratingCount: eventGetters.getReviewsTotal(kit),
            },
          }),
      }

      return obj
    })
  }

  schema.value = { '@context': 'https://schema.org', '@graph': createSchema() }

  return {
    schema,
    script,
  }
}

export default useKitProductSchema
